import {HighlightStyle} from '@codemirror/language';
import {tags as t} from '@lezer/highlight';

/**
 * VS Code Light+ highlight style — the single source of truth for light mode
 * syntax highlighting across all code editors (code block, code embed,
 * page settings, dashboard custom code, style panel custom settings).
 */
export const lightSyntaxColors = {
  keyword: '#0000FF',
  controlKeyword: '#AF00DB',
  variable: '#001080',
  constant: '#0070C1',
  classType: '#267F99',
  tag: '#800000',
  attributeName: '#E50000',
  propertyName: '#001080',
  function: '#795E26',
  number: '#098658',
  operator: '#000000',
  regexp: '#811F3F',
  string: '#A31515',
  comment: '#008000',
  link: '#0070C1',
  invalid: '#CD3131',
  escape: '#EE0000',
} as const;

const keywordColor = lightSyntaxColors.keyword;
const controlKeywordColor = lightSyntaxColors.controlKeyword;
const variableColor = lightSyntaxColors.variable;
const constantColor = lightSyntaxColors.constant;
const classTypeColor = lightSyntaxColors.classType;
const tagColor = lightSyntaxColors.tag;
const attributeNameColor = lightSyntaxColors.attributeName;
const propertyNameColor = lightSyntaxColors.propertyName;
const functionColor = lightSyntaxColors.function;
const numberColor = lightSyntaxColors.number;
const operatorColor = lightSyntaxColors.operator;
const regexpColor = lightSyntaxColors.regexp;
const stringColor = lightSyntaxColors.string;
const commentColor = lightSyntaxColors.comment;
const linkColor = lightSyntaxColors.link;
const invalidColor = lightSyntaxColors.invalid;
const escapeColor = lightSyntaxColors.escape;

export const wfCodeLightHighlightStyle = HighlightStyle.define([
  {
    tag: [
      t.keyword,
      t.operatorKeyword,
      t.modifier,
      t.color,
      t.constant(t.name),
      t.standard(t.name),
      t.special(t.brace),
      t.atom,
      t.bool,
      t.special(t.variableName),
    ],
    color: keywordColor,
  },
  {tag: [t.moduleKeyword, t.controlKeyword], color: controlKeywordColor},
  {
    tag: [
      t.name,
      t.deleted,
      t.character,
      t.macroName,
      t.variableName,
      t.labelName,
      t.definition(t.name),
    ],
    color: variableColor,
  },
  {tag: [t.propertyName], color: propertyNameColor},
  {tag: [t.attributeName], color: attributeNameColor},
  {tag: t.heading, fontWeight: 'bold', color: tagColor},
  {
    tag: [t.tagName, t.angleBracket, t.standard(t.tagName)],
    color: tagColor,
  },
  {
    tag: [
      t.typeName,
      t.className,
      t.changed,
      t.annotation,
      t.self,
      t.namespace,
    ],
    color: classTypeColor,
  },
  {
    tag: [t.function(t.variableName), t.function(t.propertyName)],
    color: functionColor,
  },
  {tag: [t.number], color: numberColor},
  {tag: [t.constant(t.variableName)], color: constantColor},
  {
    tag: [t.operator, t.punctuation, t.separator, t.url],
    color: operatorColor,
  },
  {tag: [t.escape], color: escapeColor},
  {tag: [t.regexp], color: regexpColor},
  {
    tag: [
      t.special(t.string),
      t.processingInstruction,
      t.string,
      t.inserted,
      t.attributeValue,
    ],
    color: stringColor,
  },
  {tag: [t.meta, t.comment], color: commentColor},
  {tag: t.link, color: linkColor, textDecoration: 'underline'},
  {tag: t.invalid, color: invalidColor},
  {tag: t.strong, fontWeight: 'bold'},
  {tag: t.emphasis, fontStyle: 'italic'},
  {tag: t.strikethrough, textDecoration: 'line-through'},
]);
